/**
 * Migration v1 → v2: combat genre pivot.
 *
 * # What changed
 *
 * V2 introduces real-time isometric action-roguelite combat (per the May 5,
 * 2026 genre pivot). The combat shape changed completely from v1's turn-
 * based card combat. Specifically:
 *
 *   - `combat: CombatState` (always present, turn-based) is replaced by
 *     `combat: CombatState | null` (null when no run active, populated
 *     real-time shape when a run is active).
 *
 *   - New top-level field `loadout: Loadout` (player's chosen Architecture,
 *     ability hotbar, Subroutine slots, cosmetic palette).
 *
 * # What this migration does
 *
 * For each loaded v1 save:
 *
 *   1. Validate the input shape against `LegacyGameStateV1Schema` to confirm
 *      it really is a v1 save (defensive — prevents transforming malformed
 *      input as if it were valid v1).
 *
 *   2. Replace `combat` with `null`. Any in-progress v1 run is abandoned.
 *      This is acceptable because:
 *        - V1 was card combat, not representable in v2's real-time shape.
 *        - Production never released v1 to a wide audience playing live
 *          combat runs (combat screen shipped but no real users were in
 *          mid-run when the pivot landed).
 *        - Meta-progression (currencies, unlocks — when those land) is
 *          preserved across the migration; only in-progress run state is
 *          lost.
 *
 *   3. Add `loadout: DEFAULT_LOADOUT` (Vector architecture, no Subroutines,
 *      default cosmetic palette). Player picks their preferred Architecture
 *      on next run start.
 *
 *   4. Bump `version: 1` to `version: 2`.
 *
 *   5. Sibling fields (`runSeed`, `rng`) are preserved byte-for-byte.
 *
 *   6. Envelope-level fields (`envelopeVersion`, `createdAt`, `installId`,
 *      `state.actionLog`, `state.uiPrefs`, `checksum`) are preserved
 *      byte-for-byte. The migration ONLY touches `state.gameState`.
 *
 * # Determinism
 *
 * Pure function. No clocks, no randomness, no I/O. The output for a given
 * input is byte-identical across every machine and every Node/browser
 * version. This preserves the bit-exact replay foundation Phase A
 * established.
 *
 * # Error handling
 *
 * The migration validates its input against LegacyGameStateV1Schema. If the
 * input doesn't match (corrupted save, mid-write crash, hostile tampering),
 * Zod throws and the migrations framework wraps the error as MigrationError.
 *
 * Phase B's adapter catches MigrationError and presents the user with a
 * choice: discard the save and start fresh, or restore from a rolling
 * backup (Phase B keeps the previous 3 saves).
 *
 * # The checksum field
 *
 * The migration produces a transformed payload with the OLD checksum value
 * still attached (we don't touch checksum). After migration, `serialize.ts`
 * passes the result to `SaveEnvelopeSchema.parse()`, which accepts it
 * structurally. The Phase B adapter then verifies the checksum AGAINST THE
 * ORIGINAL UNMIGRATED PAYLOAD before migration runs — so the migration's
 * output retaining the old checksum doesn't matter for integrity.
 *
 * (Caveat: the integrity check happens BEFORE migration. This is the
 * correct order — we authenticate the bytes that were actually stored,
 * not the migrated form which the original signer never saw. Phase B
 * implements this order; the migration here is correct in trusting that
 * upstream did the auth check.)
 *
 * @see {@link ../schema.ts} — LegacyGameStateV1Schema (input shape) and
 *      GameStateSchema (output shape)
 * @see {@link ../../state.ts} — DEFAULT_LOADOUT
 * @see {@link ./index.ts} — MigrationFn signature, MigrationError
 * @see {@link ./registerAll.ts} — registers this migration at boot
 */

import { z } from 'zod';

import { DEFAULT_LOADOUT } from '../../state';
import {
  LegacyGameStateV1Schema,
} from '../schema';

import type { MigrationFn } from './index';

// ─────────────────────────────────────────────────────────────────────────────
// Defensive envelope-level validation
//
// The migration receives the FULL envelope (not just gameState). We need
// to traverse to `payload.state.gameState`, validate it as v1, replace it
// with v2-shaped data, and return the wrapped envelope.
//
// Top-level envelope fields are validated only structurally (objects,
// strings) so the migration can run on slightly-malformed envelopes that
// downstream schema validation would reject anyway. The point of these
// checks is to give a clean error message identifying which field is bad,
// not to fully validate the envelope (that's `SaveEnvelopeSchema`'s job
// after migration).
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Lightweight envelope shape for the migration's input. Validates only the
 * fields the migration touches or traverses; allows other envelope fields
 * to pass through unchanged.
 *
 * Used `.passthrough()` on inner objects so unmodelled v1 envelope fields
 * survive the migration unchanged. The final v2 envelope is `.strict()`-
 * validated by the schema parser later.
 */
const V1EnvelopeShapeSchema = z
  .object({
    envelopeVersion: z.unknown(),
    createdAt: z.unknown(),
    installId: z.unknown(),
    checksum: z.unknown(),
    state: z
      .object({
        gameState: LegacyGameStateV1Schema,
        actionLog: z.unknown(),
        uiPrefs: z.unknown(),
      })
      .passthrough(),
  })
  .passthrough();

// ─────────────────────────────────────────────────────────────────────────────
// The migration function
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Transform a v1 save envelope into a v2 save envelope.
 *
 * - Validates input against `V1EnvelopeShapeSchema` (throws ZodError on bad input).
 * - Replaces gameState.combat with null.
 * - Adds gameState.loadout (DEFAULT_LOADOUT).
 * - Bumps gameState.version from 1 to 2.
 * - Preserves all other fields byte-for-byte.
 *
 * Pure function. Same input → same output. Idempotent only on v1 input
 * (calling again on v2 output throws because the version is no longer 1).
 */
export const migrateV1ToV2: MigrationFn = (input: unknown): unknown => {
  // Step 1: validate input is a v1 envelope. Zod throws ZodError on
  // mismatch; the migrations framework catches and re-wraps as
  // MigrationError.
  const v1Envelope = V1EnvelopeShapeSchema.parse(input);

  // Step 2: extract the legacy v1 gameState (now typed thanks to Zod).
  const v1GameState = v1Envelope.state.gameState;

  // Step 3: build v2 gameState. Preserve runSeed and rng byte-for-byte;
  // replace combat with null; add loadout; bump version.
  const v2GameState = {
    version: 2 as const,
    runSeed: v1GameState.runSeed,
    rng: v1GameState.rng,
    combat: null,
    loadout: DEFAULT_LOADOUT,
  };

  // Step 4: rebuild envelope with new gameState. Other state fields and
  // envelope fields are preserved unchanged.
  //
  // We use object spread to preserve any passthrough fields that may exist
  // in the original (e.g., a future v1.5 might have added optional fields
  // to state we want to keep). This is defensive but cheap — ensures no
  // information is silently dropped.
  return {
    ...v1Envelope,
    state: {
      ...v1Envelope.state,
      gameState: v2GameState,
    },
  };
};
